/**
 * Main Approvals tab container component
 * Displays table of approvals
 */
import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../../../components/Auth";
import { ApprovalsListTable, buildDefaultFilters } from "./ApprovalsList";
import { useApprovals } from "./hooks/useApprovals";
import { useApprovalsFilters } from "./hooks/useApprovalsFilters";
import { useApprovalsListControls } from "./hooks/useApprovalsListControls";
import { useSeedDefaultApproverFilter } from "./hooks/useSeedDefaultApproverFilter";
import { useSkuIdColumnHeader } from "./hooks/useSkuIdColumnHeader";
import { useStoreState } from "../../../../util/util";
import type { ApprovalWorkflow } from "../../../../types/types.Workflow";
import { useApprovalEligibilityContext } from "./ApprovalEligibilityContext";
import { useProductContext } from "../ProductContext";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

interface ApprovalsProps {
  onCountChange?: (count: number) => void;
}

export function Approvals({ onCountChange }: ApprovalsProps) {
  const product = useProductContext();
  const history = useHistory();
  const location = useLocation();
  const { storefront_id, storefront_metadata } = useStoreState();
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { registerApprovalsMutate } = useApprovalEligibilityContext();
  const productId = product.product_number || product.id;

  const {
    perPage,
    offset,
    searchQuery,
    filters,
    listFilterParams,
    onPageChange,
    onPerPageChange,
    onSearchChange,
    onClearSearch,
    onFiltersChange,
    setFilters,
  } = useApprovalsListControls(
    buildDefaultFilters(user),
    `approvals_list:${storefront_id}:product:${productId}`
  );

  const {
    approvals,
    pagination,
    isFetching,
    error,
    in_progress_approvals_count,
    total_approvals_count,
    list_details,
    mutate,
  } = useApprovals({
    productId,
    limit: perPage,
    offset,
    ...listFilterParams,
  });

  const { filters: apiFilters } = useApprovalsFilters({
    productId,
    ...listFilterParams,
  });

  useSeedDefaultApproverFilter({
    user,
    apiFilters,
    setFilters,
  });

  const skuIdHeader = useSkuIdColumnHeader({
    productSchema: product.product_schema,
    t,
  });

  useEffect(() => {
    registerApprovalsMutate(async () => {
      await mutate();
    });
  }, [mutate, registerApprovalsMutate]);

  useEffect(() => {
    if (onCountChange) {
      onCountChange(in_progress_approvals_count);
    }
  }, [in_progress_approvals_count, onCountChange]);

  const handleRowClick = (
    approval: ApprovalWorkflow,
    e: React.MouseEvent<HTMLTableRowElement>
  ) => {
    const tenantSlug = storefront_metadata?.slug;
    if (tenantSlug) {
      const url = `/store/${tenantSlug}/admin/pim/products/${product.product_number}/approvals/${approval.id}${location.search}`;
      if (e.metaKey || e.ctrlKey) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        history.push(url);
      }
    }
  };

  return (
    <Container className="product-approvals">
      <ApprovalsListTable
        variant="product"
        skuIdHeader={skuIdHeader}
        approvals={approvals}
        pagination={pagination}
        isLoading={isFetching}
        error={error}
        onRowClick={handleRowClick}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
        perPage={perPage}
        offset={offset}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        onClearSearch={onClearSearch}
        filters={filters}
        onFiltersChange={onFiltersChange}
        apiFilters={apiFilters}
        listDetails={list_details}
        totalApprovalsCount={total_approvals_count}
        onAssigneeUpdated={() => {
          void mutate();
        }}
      />
    </Container>
  );
}
